import React, { useContext, useState, useEffect } from 'react';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useParams, useHistory } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { NotificationContext } from '../../components/GrowlNotifications';
import RenewSubscription from './RenewSubscription';
import { NumberOfEmployees, SubscriptionStatus } from '../../shared/Constants';
import { Loading } from '../../components/Loading';

const SUBSCRIPTION_INFORMATION_QUERY = gql`
  query subscriptionInformationQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      displayName
      subscriptionInformation {
        currentSubscription {
          purchasedOn
          endsOn
          status
          numberOfEmployees
          duration
        }
      }
    }
  }
`;

const RENEW_SUBSCRIPTION_MUTATION = gql`
  mutation renewSubscriptionMutation($managingAccountId: ID!, $clientAccountId: ID!) {
    renewSubscription(managingAccountId: $managingAccountId, clientAccountId: $clientAccountId)
  }
`;

const i18nMessages = defineMessages({
  noLimit: {
    id: 'menu.noLimit',
    defaultMessage: 'NoLimit',
  },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  id: { id: 'attrs.id', defaultMessage: 'Id' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  organisation: {
    id: 'lbl.organisation',
    defaultMessage: 'Organisation',
  },
  renewSubscription: {
    id: 'business-center.renew-subscription',
    defaultMessage: 'Renew Subscription',
  },
  manage: {
    id: 'menu.manage',
    defaultMessage: 'Manage',
  },
  activeSubscription: {
    id: 'business-center.active-subscription',
    defaultMessage: 'Your subscription is currently active. To manage it, click the button.',
  },
});

const DEFAULT_ACCOUNT_STATE = {
  id: null,
  displayName: null,
  subscriptionInformation: {
    currentSubscription: {
      purchasedOn: null,
      endsOn: null,
      status: SubscriptionStatus.ACTIVE,
      numberOfEmployees: NumberOfEmployees._0_9,
      duration: 1,
    },
  },
};

const BuyOrReactivateOwnSubscription = () => {
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const { formatMessage } = useIntl();
  const { aid: accountId } = useParams();
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [renewSubscriptionMutation] = useMutation(RENEW_SUBSCRIPTION_MUTATION);
  const { loading, error, data } = useQuery(SUBSCRIPTION_INFORMATION_QUERY, {
    variables: {
      accountId,
    },
    fetchPolicy: 'no-cache',
  });

  const { account: requestAccount } = data;

  useEffect(() => {
    if (requestAccount) {
      setAccount(requestAccount);
    }
  }, [requestAccount]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const renewSubscription = async () => {
    try {
      await renewSubscriptionMutation({
        variables: {
          managingAccountId: accountId,
          clientAccountId: accountId,
        },
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      history.push(`/accounts/${accountId}/manage-own-subscription`);
    }
  };

  const redirectToBuySubscriptionPage = () => {
    history.push(`/buy-subscription?accountId=${accountId}`);
  };

  const {
    subscriptionInformation: {
      currentSubscription: { status },
    },
  } = account;

  return (
    <Dialog visible closable={false} header={formatMessage(i18nMessages.renewSubscription)}>
      {status !== SubscriptionStatus.PAUSED ? (
        <div className="align-center">
          <h4>{formatMessage(i18nMessages.activeSubscription)}</h4>
          <Button
            type="button"
            label={formatMessage(i18nMessages.manage)}
            onClick={() => history.push(`/accounts/${accountId}/manage-own-subscription`)}
          />
        </div>
      ) : (
        <RenewSubscription
          account={account}
          renewSubscription={renewSubscription}
          redirectToBuySubscriptionPage={redirectToBuySubscriptionPage}
        />
      )}
    </Dialog>
  );
};

export default BuyOrReactivateOwnSubscription;
